/**
 * configService – ruft den Sign-In Konfigurationsendpunkt ab.
 *
 * Der Backend-Service liest den Host-Header selbst aus und gibt
 * die passenden Options für die aktuelle Domain zurück.
 *
 * Routing-Logik (in SignInView):
 *   0 Options  → redirect zu /auth/login
 *   1 Option   → HTTP 301 redirect zur konfigurierten URL
 *   ≥2 Options → HTML Kachel-Auswahl anzeigen
 *
 * Modi (gesteuert über .env-Dateien):
 *   npm run dev        → VITE_API_URL=/user/v1/signin/config   (Vite-Proxy → localhost:8080)
 *   npm run build      → VITE_API_URL=/signin-service/v1/signin/config
 *   npm run dev:local  → VITE_USE_LOCAL_CONFIG=true → liest public/config.json direkt
 *
 * TODO (DB-Integration): JWT-Token aus Authorization-Header auslesen,
 *   E-Mail-Adresse extrahieren und Options intelligent filtern
 *   (z.B. nur Register anzeigen wenn User nicht in DB vorhanden ist).
 */

// Gesetzt per .env.development / .env.production
const API_URL = import.meta.env.VITE_API_URL ?? '/signin-service/v1/signin/config';

// Gesetzt per .env.localconfig – kein Backend nötig
const USE_LOCAL_CONFIG = import.meta.env.VITE_USE_LOCAL_CONFIG === 'true';

// Casing-tolerante Normalisierung der Options.
// Der ServiceStack-Endpunkt liefert je nach Backend-Stand camelCase ODER PascalCase
// (JsConfig.EmitCamelCaseNames), die lokale config.json camelCase. Wir mappen beide
// Varianten auf das vom Frontend erwartete camelCase-Schema.
const pick = (o, ...keys) => keys.map((k) => o?.[k]).find((v) => v !== undefined);
const normalize = (list) =>
  (Array.isArray(list) ? list : []).map((o) => ({
    name: pick(o, 'name', 'Name'),
    description: pick(o, 'description', 'Description'),
    url: pick(o, 'url', 'Url'),
    type: (pick(o, 'type', 'Type') ?? '').toString().toLowerCase(),
    icon: pick(o, 'icon', 'Icon'),
  }));

/**
 * Lädt die Sign-In Options für die aktuelle Domain.
 * Je nach Build-Modus entweder vom Backend oder aus der lokalen config.json.
 *
 * @returns {Promise<Array<{name: string, description: string, url: string, type: string, icon: string}>>}
 */
async function getOptions() {
  // Priorität 1: Server-seitig injizierte Konfiguration (ASPX-Shell)
  // window.__SIGNIN_CONFIG__ wird vom ASPX Code-Behind gesetzt,
  // sodass kein öffentlicher API-Endpunkt benötigt wird.
  if (typeof window.__SIGNIN_CONFIG__ !== 'undefined') {
    return normalize(window.__SIGNIN_CONFIG__);
  }

  // Priorität 2: Lokale config.json (dev:local Modus)
  if (USE_LOCAL_CONFIG) {
    return getOptionsFromLocalConfig();
  }

  // Priorität 3: Backend API
  const response = await fetch(API_URL);
  if (!response.ok) {
    throw new Error(`Konfiguration konnte nicht geladen werden (HTTP ${response.status})`);
  }
  return normalize(await response.json());
}

/**
 * Liest die Options aus der lokalen public/config.json (kein Backend nötig).
 * Wendet dieselbe Hostname-Matching-Logik an wie das Backend.
 */
async function getOptionsFromLocalConfig() {
  const response = await fetch('/signin/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Lokale Konfiguration konnte nicht geladen werden (HTTP ${response.status})`);
  }

  const config = await response.json();
  const hostname = window.location.hostname;

  const hostEntry = (config.hosts ?? []).find(
    (h) => h.hostname.toLowerCase() === hostname.toLowerCase()
  );

  return normalize(hostEntry?.options ?? config.defaultOptions ?? []);
}

export default {
  getOptions,
};
